/* ═══════════════════════════════════════════════════════════════════════
   NoteFlow — auth-2fa.ts
   Two-factor authentication: setup, verification, sign-in challenge.
   ═══════════════════════════════════════════════════════════════════════ */

import { state } from './state';
import { FIREBASE_ENABLED } from './config';
import { esc, el, elInput } from './utils';
import { authMsg, showAuthView, mfaState } from './auth-helpers';
import { loginSession } from './auth-signin';

declare const firebase: any;

// ─── Security Settings UI ───────────────────────────────────────────────────

export function openSecuritySettings(): void {
  el('security-modal')?.classList.add('open');
  const emailEl = el('security-email');
  if (emailEl) emailEl.textContent = state.currentUserEmail || '';
  const msgEl = el('2fa-msg');
  if (msgEl) msgEl.innerHTML = '';
  check2FAStatus();
}

export function closeSecuritySettings(): void {
  el('security-modal')?.classList.remove('open');
}

function check2FAStatus(): void {
  if (!FIREBASE_ENABLED || !state.firebaseAuth.currentUser) {
    const statusEl = el('2fa-status');
    if (statusEl) {
      statusEl.textContent = 'Unavailable';
      statusEl.style.background = '#f1f5f9';
      statusEl.style.color = '#64748b';
    }
    const actionBtn = el('2fa-action-btn');
    if (actionBtn) actionBtn.style.display = 'none';
    return;
  }

  const user = state.firebaseAuth.currentUser;
  const enrolled: boolean = user.multiFactor && user.multiFactor.enrolledFactors && user.multiFactor.enrolledFactors.length > 0;

  if (enrolled) {
    const statusEl = el('2fa-status');
    if (statusEl) {
      statusEl.textContent = 'Enabled';
      statusEl.style.background = '#dcfce7';
      statusEl.style.color = '#16a34a';
    }
    const actionBtn = el('2fa-action-btn');
    if (actionBtn) actionBtn.style.display = 'none';
    const disableBtn = el('2fa-disable-btn');
    if (disableBtn) disableBtn.style.display = 'inline-block';
    const setupForm = el('2fa-setup-form');
    const loginField = setupForm?.querySelector('.login-field') as HTMLElement | null;
    if (loginField) loginField.setAttribute('style', 'display:none');
  } else {
    const statusEl = el('2fa-status');
    if (statusEl) {
      statusEl.textContent = 'Disabled';
      statusEl.style.background = '#fee2e2';
      statusEl.style.color = '#dc2626';
    }
    const actionBtn = el('2fa-action-btn');
    if (actionBtn) {
      actionBtn.style.display = 'inline-block';
      actionBtn.textContent = 'Enable 2FA';
      (actionBtn as HTMLElement).onclick = start2FASetup;
    }
    const disableBtn = el('2fa-disable-btn');
    if (disableBtn) disableBtn.style.display = 'none';
    const setupForm = el('2fa-setup-form');
    const loginField = setupForm?.querySelector('.login-field') as HTMLElement | null;
    if (loginField) loginField.setAttribute('style', 'display:block');
    const verifySection = el('2fa-verify-section');
    if (verifySection) verifySection.style.display = 'none';
  }
}

export function start2FASetup(): void {
  let phone: string = elInput('2fa-phone')?.value.trim() || '';
  if (!phone) {
    const msgEl = el('2fa-msg');
    if (msgEl) msgEl.innerHTML = '<div class="login-msg error">' + esc('Please enter a phone number.') + '</div>';
    return;
  }
  if (!phone.startsWith('+')) {
    phone = '+1' + phone.replace(/[^0-9]/g, '');
  }

  const msgEl2fa = el('2fa-msg');
  if (msgEl2fa) msgEl2fa.innerHTML = '<div class="login-msg info">' + esc('Sending verification code...') + '</div>';

  if (!mfaState.recaptchaVerifier) {
    mfaState.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container', {
      size: 'invisible',
    });
  }

  const user = state.firebaseAuth.currentUser;
  user.multiFactor.getSession().then(function (session: any) {
    const phoneOpts = {
      phoneNumber: phone,
      session: session,
    };
    const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
    return phoneAuthProvider.verifyPhoneNumber(phoneOpts, mfaState.recaptchaVerifier);
  }).then(function (verificationId: string) {
    mfaState.mfaVerificationId = verificationId;
    const verifySection = el('2fa-verify-section');
    if (verifySection) verifySection.style.display = 'block';
    const actionBtn = el('2fa-action-btn');
    if (actionBtn) {
      actionBtn.textContent = 'Verify Code';
      (actionBtn as HTMLElement).onclick = verify2FACode;
    }
    const msgEl = el('2fa-msg');
    if (msgEl) msgEl.innerHTML = '<div class="login-msg success">' + esc('Code sent! Check your phone.') + '</div>';
  }).catch(function (err: any) {
    console.error('2FA setup error:', err);
    const msg: string = err.code === 'auth/requires-recent-login'
      ? 'Please sign out and sign back in, then try again.'
      : err.message;
    const msgEl = el('2fa-msg');
    if (msgEl) msgEl.innerHTML = '<div class="login-msg error">' + esc(msg) + '</div>';
  });
}

function verify2FACode(): void {
  const code: string = elInput('2fa-code')?.value.trim() || '';
  if (!code || code.length !== 6) {
    const msgEl = el('2fa-msg');
    if (msgEl) msgEl.innerHTML = '<div class="login-msg error">' + esc('Please enter the 6-digit code.') + '</div>';
    return;
  }

  const msgEl = el('2fa-msg');
  if (msgEl) msgEl.innerHTML = '<div class="login-msg info">' + esc('Verifying...') + '</div>';

  const cred = firebase.auth.PhoneAuthProvider.credential(mfaState.mfaVerificationId, code);
  const multiFactorAssertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);

  state.firebaseAuth.currentUser.multiFactor.enroll(multiFactorAssertion, 'Phone').then(function () {
    const m = el('2fa-msg');
    if (m) m.innerHTML = '<div class="login-msg success">' + esc('Two-factor authentication enabled!') + '</div>';
    check2FAStatus();
  }).catch(function (err: any) {
    const m = el('2fa-msg');
    if (m) m.innerHTML = '<div class="login-msg error">' + esc(err.message) + '</div>';
  });
}

export function disable2FA(): void {
  if (!confirm('Disable two-factor authentication? Your account will be less secure.')) return;

  const user = state.firebaseAuth.currentUser;
  if (user.multiFactor.enrolledFactors.length > 0) {
    const factor = user.multiFactor.enrolledFactors[0];
    user.multiFactor.unenroll(factor).then(function () {
      const msgEl = el('2fa-msg');
      if (msgEl) msgEl.innerHTML = '<div class="login-msg success">' + esc('Two-factor authentication disabled.') + '</div>';
      check2FAStatus();
    }).catch(function (err: any) {
      const msg: string = err.code === 'auth/requires-recent-login'
        ? 'Please sign out and sign back in, then try again.'
        : err.message;
      const msgEl = el('2fa-msg');
      if (msgEl) msgEl.innerHTML = '<div class="login-msg error">' + esc(msg) + '</div>';
    });
  }
}

// ─── Sign-in challenge (called from auth-signin when MFA required) ──────────

export function show2FASignInChallenge(): void {
  showAuthView('2fa');

  if (!mfaState.recaptchaVerifier) {
    mfaState.recaptchaVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container', { size: 'invisible' });
  }

  const hint = mfaState.mfaResolver.hints[0];
  const phoneAuthProvider = new firebase.auth.PhoneAuthProvider();
  phoneAuthProvider.verifyPhoneNumber({
    multiFactorHint: hint,
    session: mfaState.mfaResolver.session,
  }, mfaState.recaptchaVerifier).then(function (verificationId: string) {
    mfaState.mfaVerificationId = verificationId;
    authMsg('2fa-signin-msg', 'success', 'Code sent to ' + hint.phoneNumber);
  }).catch(function (err: any) {
    authMsg('2fa-signin-msg', 'error', err.message);
  });
}

export function verify2FASignIn(): void {
  const code: string = elInput('2fa-signin-code')?.value.trim() || '';
  if (!code || code.length !== 6) {
    authMsg('2fa-signin-msg', 'error', 'Please enter the 6-digit code.');
    return;
  }

  authMsg('2fa-signin-msg', 'info', 'Verifying...');

  const cred = firebase.auth.PhoneAuthProvider.credential(mfaState.mfaVerificationId, code);
  const assertion = firebase.auth.PhoneMultiFactorGenerator.assertion(cred);

  mfaState.mfaResolver.resolveSignIn(assertion).then(function (userCred: any) {
    const name: string = userCred.user.displayName || userCred.user.email;
    loginSession(userCred.user.email, name);
  }).catch(function (_err: any) {
    authMsg('2fa-signin-msg', 'error', 'Invalid code. Please try again.');
  });
}
